import { Link } from "react-router-dom";
import { 
  ArrowRight, 
  Users, 
  Target, 
  TrendingUp, 
  Award,
  CheckCircle,
  ArrowUpRight
} from "lucide-react";

const HomePage = () => {
  const macroServices = [
    {
      icon: Users,
      title: "Recrutamento & Avaliação",
      description: "Seleção estratégica com mapeamento de perfil, entrevistas por competências e avaliação de fit cultural.",
      link: "/servicos#recrutamento"
    },
    {
      icon: Target,
      title: "Liderança & Treinamentos",
      description: "Desenvolvimento de líderes com inteligência emocional, comunicação assertiva e gestão de equipes.",
      link: "/servicos#lideranca"
    },
    {
      icon: TrendingUp,
      title: "Consultoria em Gestão",
      description: "Estruturação de cultura organizacional, processos de RH e políticas de gestão de pessoas.",
      link: "/servicos#consultoria"
    },
    {
      icon: Award,
      title: "Desenvolvimento de Equipes",
      description: "Programas personalizados para alinhar desempenho, bem-estar e resultados sustentáveis.",
      link: "/servicos#desenvolvimento"
    }
  ];

  const diferenciais = [
    {
      num: "01",
      title: "Diagnóstico qualificado",
      description: "Intervenções baseadas em evidência, critério e método. Sem fórmulas genéricas ou soluções padronizadas."
    },
    {
      num: "02",
      title: "Transformação personalizada",
      description: "Cada empresa recebe uma abordagem singular, respeitando identidade, maturidade e objetivos próprios."
    },
    {
      num: "03",
      title: "Resultado sustentável",
      description: "O foco está em construir consistência no longo prazo, não em soluções paliativas ou intervenções pontuais."
    }
  ];

  const tags = [
    { label: "Humana", color: "bg-[#D9C4B8] text-brand-terracota" },
    { label: "Técnica", color: "bg-[#C5CBAF] text-brand-verde" },
    { label: "Estratégica", color: "bg-[#D9C4B8] text-brand-neutro" },
    { label: "Confiável", color: "bg-brand-neutro text-brand-base-clara" }
  ];

  const plans = [
    {
      name: "Essencial",
      price: "497",
      description: "Para empresas que estão iniciando a estruturação de pessoas",
      features: ["Diagnóstico inicial", "1 reunião mensal de acompanhamento", "Suporte por e-mail"]
    },
    {
      name: "Crescimento",
      price: "997",
      description: "Para empresas em expansão que precisam escalar com qualidade",
      features: ["Tudo do Essencial", "2 reuniões mensais", "Treinamento trimestral", "Suporte prioritário"],
      popular: true
    },
    {
      name: "Performance",
      price: "1.997",
      description: "Para organizações que buscam alto desempenho consistente",
      features: ["Tudo do Crescimento", "4 reuniões mensais", "Treinamento mensal", "Avaliações de equipe"]
    },
    {
      name: "Estratégico Premium",
      price: "3.497",
      description: "Acompanhamento completo e personalizado para transformação",
      features: ["Acompanhamento semanal", "Consultoria dedicada", "Treinamentos ilimitados", "Suporte 24/7"]
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20 lg:pt-0 overflow-hidden" data-testid="hero-section">
        {/* Gradient Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-base-clara via-[#E5DBC5] to-brand-surface" />
        
        {/* Subtle diagonal texture */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `repeating-linear-gradient(
            45deg,
            transparent,
            transparent 100px,
            rgba(184, 142, 106, 0.03) 100px,
            rgba(184, 142, 106, 0.03) 200px
          )`
        }} />

        {/* Decorative vertical lines */}
        <div className="absolute right-12 lg:right-24 top-1/4 bottom-1/4 flex gap-3">
          <div className="w-[2px] h-full bg-brand-terra-humana/30" />
          <div className="w-[2px] h-full bg-brand-terracota/40 mt-12" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
          <div className="max-w-4xl">
            <p className="animate-fade-up font-sans text-sm tracking-[0.15em] text-brand-muted mb-8 flex items-center gap-3">
              <span className="w-6 h-[1px] bg-brand-muted" />
              LYLIAN LOPES CONSULTORIA
            </p>
            <h1 className="animate-fade-up-delay-1 font-serif text-4xl sm:text-5xl lg:text-6xl font-normal text-brand-neutro leading-[1.15] mb-8">
              Desenvolvendo{" "}
              <span className="italic text-brand-terracota">pessoas, liderança e cultura</span>
              {" "}com consistência.
            </h1>
            <p className="animate-fade-up-delay-2 font-sans text-base lg:text-lg text-brand-muted leading-relaxed mb-10 max-w-2xl">
              Diagnóstico, seleção, treinamentos, liderança e estruturação de gestão de pessoas. Mais clareza sobre gente, mais sustentabilidade no crescimento do negócio.
            </p>
            <div className="animate-fade-up-delay-3 flex flex-col sm:flex-row gap-4">
              <a
                href="https://wa.me/5586998325746?text=Olá! Gostaria de agendar uma conversa sobre os serviços da consultoria."
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-sans font-medium text-base"
                data-testid="hero-cta-primary"
              >
                Agendar conversa
                <ArrowRight size={18} />
              </a>
              <Link
                to="/servicos"
                className="btn-secondary inline-flex items-center justify-center gap-2 px-8 py-4 rounded-xl font-sans font-medium text-base"
                data-testid="hero-cta-secondary"
              >
                Conhecer serviços
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Propósito e Posicionamento Section */}
      <section className="py-24 lg:py-32 bg-brand-surface" data-testid="proposito-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <p className="font-sans text-sm tracking-[0.15em] text-brand-muted mb-12 flex items-center gap-3">
            <span className="w-6 h-[1px] bg-brand-muted" />
            PROPÓSITO E POSICIONAMENTO
          </p>
          
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Left side - Quote */}
            <div>
              <div className="flex items-start gap-4">
                <div className="w-[3px] h-24 bg-brand-terra-humana/40 flex-shrink-0 mt-2" />
                <blockquote className="font-serif text-3xl lg:text-4xl italic text-brand-terra-humana leading-snug">
                  Empresas crescem quando pessoas crescem.
                </blockquote>
              </div>
            </div>

            {/* Right side - Text */}
            <div className="space-y-6">
              <p className="font-sans text-brand-neutro leading-relaxed">
                A consultoria se posiciona no encontro entre ciência psicológica, leitura comportamental e gestão estratégica. O resultado pretendido não é apenas organização de processos, mas transformação sustentável de pessoas, lideranças e cultura.
              </p>
              <p className="font-sans text-brand-muted leading-relaxed">
                Decisões de gente mais assertivas para crescer com consistência. Estruture pessoas e cultura sem perder a identidade da empresa.
              </p>
            </div>
          </div>

          {/* Tags */}
          <div className="flex flex-wrap gap-4 mt-16 justify-center items-center">
            {tags.map((tag, i) => (
              <span 
                key={i}
                className={`px-8 py-6 rounded-[40px] font-serif text-xl lg:text-2xl font-normal ${tag.color}`}
              >
                {tag.label}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Por que escolher Section - Dark */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="porqueeescolher-section">
        {/* Gradient background */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-neutro via-[#5A3A30] to-[#3D251C]" />
        
        {/* Subtle radial accents */}
        <div className="absolute inset-0 opacity-30" style={{
          backgroundImage: `radial-gradient(circle at 10% 20%, rgba(184, 142, 106, 0.15) 0%, transparent 40%),
                           radial-gradient(circle at 90% 80%, rgba(160, 81, 53, 0.1) 0%, transparent 40%)`
        }} />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-6 flex items-center gap-3">
            <span className="w-6 h-[1px] bg-brand-terracota" />
            POR QUE ESCOLHER
          </p>
          
          <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl font-normal text-brand-base-clara leading-tight mb-16 max-w-3xl">
            Aliança entre ciência psicológica, comportamento e gestão
          </h2>

          <div className="grid md:grid-cols-3 gap-12 lg:gap-16">
            {diferenciais.map((item, index) => (
              <div key={index} data-testid={`diferencial-${index}`}>
                <span className="font-serif text-5xl lg:text-6xl text-brand-terra-humana/50 block mb-4">
                  {item.num}
                </span>
                <h3 className="font-sans text-lg font-semibold text-brand-base-clara mb-3">
                  {item.title}
                </h3>
                <p className="font-sans text-brand-terra-humana/80 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manifesto CTA Section */}
      <section className="py-32 lg:py-40 relative overflow-hidden" data-testid="manifesto-section">
        {/* Gradient Background with diagonal texture */}
        <div className="absolute inset-0 bg-gradient-to-br from-brand-base-clara via-[#E2D6C0] to-brand-surface" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `repeating-linear-gradient(
            -45deg,
            transparent,
            transparent 80px,
            rgba(184, 142, 106, 0.05) 80px,
            rgba(184, 142, 106, 0.05) 160px
          )`
        }} />

        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-4xl lg:text-5xl xl:text-6xl font-normal text-brand-neutro leading-tight mb-8">
            <span className="italic text-brand-terracota">Desenvolvimento humano</span>
            {" "}com direção estratégica.
          </h2>
          
          <div className="w-12 h-[2px] bg-brand-terracota mx-auto mb-8" />
          
          <p className="font-sans text-lg text-brand-muted mb-12">
            Desenvolva líderes com mais clareza, equilíbrio e direção.
          </p>

          <a
            href="https://wa.me/5586998325746?text=Olá! Gostaria de saber mais sobre o desenvolvimento de líderes."
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary inline-flex items-center justify-center gap-2 px-10 py-4 rounded-xl font-sans font-medium text-base"
            data-testid="manifesto-cta"
          >
            Agendar conversa
            <ArrowRight size={18} />
          </a>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 lg:py-32 relative overflow-hidden" data-testid="services-section">
        {/* Gradient background with subtle diamond texture */}
        <div className="absolute inset-0 bg-gradient-to-b from-brand-base-clara via-[#DDD5C5] to-brand-surface" />
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%234C2B21' fill-opacity='1'%3E%3Cpath d='M30 0L60 30L30 60L0 30L30 0zm0 4.24L4.24 30L30 55.76L55.76 30L30 4.24z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          backgroundSize: '40px 40px'
        }} />
        
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-4 flex items-center gap-3 justify-center">
              <span className="w-6 h-[1px] bg-brand-terracota" />
              ÁREAS DE ATUAÇÃO
              <span className="w-6 h-[1px] bg-brand-terracota" />
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-neutro mb-4">
              Soluções estratégicas para cada desafio
            </h2>
            <p className="font-sans text-brand-muted leading-relaxed">
              Não basta contratar. É preciso alinhar, desenvolver e sustentar.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
            {macroServices.map((service, index) => (
              <Link
                key={index}
                to={service.link}
                className="group bg-brand-surface p-8 lg:p-10 rounded-2xl card-hover border border-transparent hover:border-brand-terracota/20"
                data-testid={`service-card-${index}`}
              >
                <div className="w-12 h-12 rounded-xl bg-brand-terra-humana/20 flex items-center justify-center mb-6 group-hover:bg-brand-terracota/20 transition-premium">
                  <service.icon className="text-brand-terracota" size={24} strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-xl lg:text-2xl font-medium text-brand-neutro mb-3">
                  {service.title}
                </h3>
                <p className="font-sans text-brand-muted leading-relaxed mb-4">
                  {service.description}
                </p>
                <span className="inline-flex items-center gap-2 font-sans text-sm text-brand-terracota font-medium group-hover:gap-3 transition-all">
                  Saiba mais <ArrowRight size={16} />
                </span>
              </Link>
            ))}
          </div>

          <div className="relative text-center mt-12">
            <Link
              to="/servicos"
              className="btn-secondary inline-flex items-center gap-2 px-8 py-4 rounded-xl font-sans font-medium"
              data-testid="services-cta"
            >
              Ver todos os serviços
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Plans Preview Section */}
      <section className="py-24 lg:py-32 bg-gradient-to-b from-brand-surface to-brand-base-clara" data-testid="plans-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <p className="font-sans text-sm tracking-[0.15em] text-brand-terracota mb-4 flex items-center gap-3 justify-center">
              <span className="w-6 h-[1px] bg-brand-terracota" />
              PLANOS DE ACOMPANHAMENTO
              <span className="w-6 h-[1px] bg-brand-terracota" />
            </p>
            <h2 className="font-serif text-3xl lg:text-4xl font-normal text-brand-neutro mb-4">
              Escolha o nível de suporte ideal
            </h2>
            <p className="font-sans text-brand-muted leading-relaxed">
              Planos flexíveis que crescem junto com sua organização.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {plans.map((plan, index) => (
              <div 
                key={index}
                className={`bg-brand-base-clara p-6 lg:p-8 rounded-2xl relative ${plan.popular ? 'plan-highlight' : 'border border-brand-border'}`}
                data-testid={`plan-card-${index}`}
              >
                <h3 className="font-serif text-xl font-medium text-brand-neutro mb-2">
                  {plan.name}
                </h3>
                <p className="font-sans text-sm text-brand-muted mb-4">
                  {plan.description}
                </p>
                <div className="mb-6">
                  <span className="font-sans text-sm text-brand-muted">R$</span>
                  <span className="font-serif text-4xl font-semibold text-brand-neutro">{plan.price}</span>
                  <span className="font-sans text-sm text-brand-muted">/mês</span>
                </div>
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-2 font-sans text-sm text-brand-neutro">
                      <CheckCircle className="text-brand-verde flex-shrink-0 mt-0.5" size={16} />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/planos"
                  onClick={() => window.scrollTo(0, 0)}
                  className={`w-full inline-flex items-center justify-center gap-2 py-3 rounded-xl font-sans text-sm font-medium transition-premium ${
                    plan.popular 
                      ? 'btn-primary' 
                      : 'btn-secondary'
                  }`}
                  data-testid={`plan-cta-${index}`}
                >
                  Conhecer plano
                </Link>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/planos"
              className="font-sans text-brand-terracota font-medium link-underline"
              data-testid="plans-details-link"
            >
              Ver detalhes completos dos planos
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 lg:py-32 bg-brand-terracota relative overflow-hidden" data-testid="final-cta-section">
        {/* Subtle pattern overlay */}
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(232, 223, 198, 0.3) 0%, transparent 50%),
                           radial-gradient(circle at 80% 50%, rgba(232, 223, 198, 0.2) 0%, transparent 50%)`
        }} />
        
        <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl xl:text-5xl font-normal text-brand-base-clara mb-6">
            Crescimento empresarial exige estrutura humana.
          </h2>
          <p className="font-sans text-lg text-brand-base-clara/80 leading-relaxed mb-10 max-w-2xl mx-auto">
            Agende um diagnóstico estratégico gratuito e descubra como alinhar pessoas, cultura e resultados na sua empresa.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://wa.me/5586998325746?text=Olá! Gostaria de agendar um diagnóstico estratégico para minha empresa."
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center justify-center gap-2 bg-brand-base-clara text-brand-neutro hover:bg-white px-8 py-4 rounded-xl font-sans font-medium transition-premium"
              data-testid="final-cta-primary"
            >
              Agendar Diagnóstico Gratuito
              <ArrowUpRight size={18} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <Link
              to="/contato"
              className="inline-flex items-center justify-center gap-2 bg-transparent border border-brand-base-clara/50 text-brand-base-clara hover:bg-brand-base-clara/10 px-8 py-4 rounded-xl font-sans font-medium transition-premium"
              data-testid="final-cta-secondary"
            >
              Fale Conosco
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
